interface HelpCopy {
  title: string;
  body: string;
  steps: string[];
}

const COPY: Readonly<Record<string, HelpCopy>> = {
  dashboard: {
    title: 'Your day at a glance',
    body: 'The dashboard shows what is happening now: consultations started, new patients, bed occupancy, money collected, admissions, discharges and the lab and imaging work still pending. The cards and the menu follow your plan and your role, so you only see the areas you work in.',
    steps: [
      'Read the cards from the top: today’s patients and money first, then the work still pending.',
      'Click a card to open the screen behind it, for example pending lab work opens the laboratory worklist.',
      'Use Reports for any other date range; the dashboard always shows today.',
    ],
  },
  queue: {
    title: 'The queue and the vitals',
    body: 'The queue lists who is waiting, for how long, and whether vitals are recorded or a fee is due. Patients join it when the desk marks them arrived, in the order they came, with a token number.',
    steps: [
      'Pick the doctor or the department you are working for at the top.',
      'Open a patient and record blood pressure, pulse, temperature, oxygen saturation and weight.',
      'Press Start to open the consultation; the doctor can also start it from here.',
      'A fee-due badge means the consultation charge has not been paid yet; the cashier settles it from OPD dues.',
    ],
  },
  triage: {
    title: 'Vitals screening and hand-off',
    body: 'Triage is where a nurse screens arrivals before the doctor sees them: vitals, an acuity, and a note for the doctor. Anyone with the right role can take vitals, so the patient is ready when the doctor calls them in.',
    steps: [
      'Choose the patient from the list of arrivals.',
      'Record the vitals and pick the acuity that matches what you see.',
      'Save; the patient moves on to the doctor’s queue with the vitals already attached.',
    ],
  },
  cosign: {
    title: 'Co-sign a junior’s consultation',
    body: 'Consultations closed by medical officers wait here for a consultant’s counter-signature. Nothing about the note changes; the signature records that a senior doctor reviewed it.',
    steps: [
      'Open a consultation from the list to read the note, the diagnosis and the orders.',
      'Press Co-sign to add your signature, or send it back with a comment if something needs changing.',
    ],
  },
  appointments: {
    title: 'Book, arrive and manage appointments',
    body: 'Find the patient, choose the doctor and the date, and the free slots appear. Slots come straight from the clinic hours, so nobody can book a time the doctor does not work; a full slot needs an explicit overbook seat. When the patient reaches the desk, press Arrived and they join the doctor’s queue with a token number.',
    steps: [
      'Press Book, search the patient, pick the doctor and a free slot, note the reason and confirm.',
      'On the day, press Arrived when the patient reaches the desk; No-show and Cancel sit beside it.',
      'Use Reschedule to move a booking to another free slot without cancelling it.',
      'The status column always tells the truth about the day, so nobody waits for a patient who is not coming.',
    ],
  },
  schedules: {
    title: 'Publish clinic hours before anyone can book',
    body: 'Until a doctor has a weekly template, the booking screen has no slots to offer. A template says which days and hours the doctor sits, where, and how long each slot is. Publishing again replaces the doctor’s current template; appointments already booked keep their times.',
    steps: [
      'Choose the doctor and the branch.',
      'Add a session for each day: start time, end time, slot length and how many patients a slot holds.',
      'Publish. The booking screen offers these slots from the next day onwards.',
      'Add a block for leave or a conference so those days are not bookable.',
    ],
  },
  'follow-ups': {
    title: 'Patients a doctor asked to see again',
    body: 'When a doctor records a follow-up date in the consultation, the patient appears here as the date approaches. The desk uses the list to call patients and book them in before they are overdue.',
    steps: [
      'Switch between due, overdue and booked with the tabs.',
      'Press Book beside a patient to open booking with the patient and doctor already filled in.',
      'Mark a follow-up as not needed if the doctor says so, and it leaves the list.',
    ],
  },
  'online-requests': {
    title: 'Requests from the public directory',
    body: 'Patients who find you in the public directory can ask for an appointment or a lab visit. Each request waits here until someone at the desk accepts it, which books it, or declines it with a reason the patient sees.',
    steps: [
      'Open a request to see who asked, for what and when.',
      'Accept to turn it into a booking, matching the patient to an existing record or registering them.',
      'Decline with a short, polite reason when you cannot fit it in.',
    ],
  },
  reports: {
    title: 'Reports for any date range',
    body: 'Daily census, revenue, collections, receivables, doctors, diagnoses and KPIs, each for the dates you choose. Collections and receivables are built from the invoices and payments your cashiers record, so they match the drawer.',
    steps: [
      'Pick a report tab, then set the date range and the branch.',
      'Export the table when the accountant needs it in a spreadsheet.',
      'The Builder tab composes a report of your own from the datasets your modules provide.',
    ],
  },
  patients: {
    title: 'Find a patient in a few keystrokes',
    body: 'Search by MRN, name, phone number or national ID. The list narrows as you type. There is one lifetime record per person, so a returning patient keeps their whole history; always search before you register.',
    steps: [
      'Type any part of the name, the MRN or the phone number.',
      'Open the record to see visits, bills, documents and allergies.',
      'If nobody matches, press Register to create a new record.',
    ],
  },
  'patients.new': {
    title: 'Check first, then register once',
    body: 'Search the national ID or mobile number before anything else, so the same person is never entered twice. One mobile number usually covers a whole family, so pick the right person by name from what comes back. If nobody comes back, fill in the identity and contact details and press Register.',
    steps: [
      'Enter the mobile number or national ID and wait for the match list.',
      'If the patient is already here, open their record instead of registering again.',
      'Otherwise fill in name, sex, date of birth or an approximate age, and contact details.',
      'Press Register; the chart opens with a new MRN and Book appointment is one click away.',
    ],
  },
  mrd: {
    title: 'Medical records',
    body: 'What is unfinished, what is coded, and who asked for a copy. Records clerks complete and code closed visits here, and release copies only with the patient’s consent on file.',
    steps: [
      'Work the incomplete list first: visits closed without the documents the record needs.',
      'Open a record to add ICD codes to its diagnoses.',
      'Log a request for a copy, attach the consent, and release it when the request is approved.',
    ],
  },
  consultations: {
    title: 'The consultation screen',
    body: 'Complaints, history, examination and plan are written in one place, and the note autosaves as you type. Diagnosis, prescription and orders sit on the same screen; the tests you order reach the laboratory the moment you place them, and their bill is raised for you.',
    steps: [
      'Write the note; previous visits are one tab away if you need them.',
      'Search the ICD-10 list by name or code and add at least one diagnosis.',
      'Write the prescription; allergy and interaction warnings appear as you type, and a severe one must be acknowledged.',
      'Order laboratory or imaging tests, set the priority and place the order.',
      'Close the consultation. This marks the appointment completed and locks the note.',
    ],
  },
  'ipd.beds': {
    title: 'Every bed in the hospital at a glance',
    body: 'The bed board counts total, occupied, vacant and awaiting-cleaning beds, then lists each ward with its beds and their state. The occupancy figure on the dashboard is worked out from these same beds.',
    steps: [
      'Filter to one ward when you only care about one floor.',
      'Click a bed to move it between housekeeping states or to see who is in it.',
      'Vacant beds of the right class are what the admission dialog offers.',
    ],
  },
  'ipd.admissions': {
    title: 'Admit and follow an in-patient',
    body: 'Admitting occupies the bed, takes the advance deposit and opens the running bill together. The admission page is one place for the whole stay: orders, eMAR, notes, vitals, transfers and the discharge summary.',
    steps: [
      'Press Admit, choose the patient, the bed class and a free bed, the consultant and a provisional diagnosis, and take the deposit.',
      'Open an admission to write physician orders; the dose schedule is generated from the times you give.',
      'Nurses record each due dose on the eMAR and write progress and nursing notes on the Notes tab.',
      'Transfer bed when the patient moves; the tariff changes from the moment of transfer.',
      'Discharge with a summary; the bed goes back to the ward for cleaning.',
    ],
  },
  'ipd.wards': {
    title: 'The ward task board',
    body: 'Everything the nurses on a ward have to do now: doses, drip and oxygen checks, vitals rounds, meals and other ordered care, for every admitted patient, grouped as overdue, due now and the next two hours. Tasks move between the groups on their own as time passes, and the board updates when anyone records one.',
    steps: [
      'Pick your ward; if the roster puts you on one ward only, it opens straight away.',
      'Work from the Overdue group first, then Due now; each task shows the bed, the patient and what was ordered.',
      'Press Record to chart what happened: done or given, held, refused or not done, with a reason and any readings.',
      'Open a patient name to see the full task chart of that admission.',
    ],
  },
  'ipd.locator': {
    title: 'Find a patient for a visitor',
    body: 'Where an admitted patient is, for visitors: ward, room and bed. The screen shows nothing clinical, so it is safe to use at the front gate.',
    steps: [
      'Type the patient’s name or MRN.',
      'Read the ward and bed from the result and direct the visitor.',
    ],
  },
  'ipd.charge-rules': {
    title: 'Charges added to every admitted patient',
    body: 'Ward charge rules are the charges the system adds to every admitted patient automatically: the bed day, nursing, and anything else the ward bills by the day. The bed charge follows the class of the bed the patient is in.',
    steps: [
      'Add a rule per bed class or ward with the service it bills and how often.',
      'Switch a rule off rather than deleting it when it no longer applies.',
      'Prices come from Services & prices, so change the amount there.',
    ],
  },
  er: {
    title: 'The emergency board',
    body: 'Sickest first, then longest waiting. Registration must never delay care, so an unidentified patient can be registered as unknown and matched to a record later. Every arrival is triaged, treated and given a disposition: home, admit or refer.',
    steps: [
      'Register an arrival, with a name if you have one and as unknown if you do not.',
      'Triage with an acuity; the board orders itself by it.',
      'Record the disposition when the patient leaves the department.',
    ],
  },
  ot: {
    title: 'The theatre list',
    body: 'The day’s list, with the safety checklist that gates it. Overlapping bookings in the same theatre are refused, and a case cannot start until the checklist is complete.',
    steps: [
      'Schedule a case: patient, procedure, theatre, surgeon and time.',
      'Complete the sign-in, time-out and sign-out checklist as the case progresses.',
      'Record implants and the operative note before closing the case.',
    ],
  },
  'blood-bank': {
    title: 'Donors, stock, cross-match and transfusion',
    body: 'Donors are registered and screened, units are received as components with their shelf life, and a cross-match offers only units the patient’s group can receive.',
    steps: [
      'Register a donor and record their screening result.',
      'Receive units as components; the expiry is set from the component.',
      'Request a cross-match for a patient, then issue and record the transfusion.',
    ],
  },
  maternity: {
    title: 'Labour and delivery',
    body: 'The partograph, delivery notes, the newborn record and the birth register. A labour room is a ward: it admits, charges bed days and discharges.',
    steps: [
      'Admit the mother to the labour room.',
      'Chart the partograph during labour.',
      'Record the delivery and register each newborn; the newborn gets its own record linked to the mother.',
    ],
  },
  therapy: {
    title: 'Therapy units',
    body: 'Dialysis sessions and day-care chairs: allocate a machine or chair, chart the session and bill it. Each session is charged on its own, so the bill is right even when a patient comes three times a week.',
    steps: [
      'Book or start a session for the patient on a free machine or chair.',
      'Chart the session as it runs.',
      'Close the session; its charge is raised automatically.',
    ],
  },
  dental: {
    title: 'Dental',
    body: 'Tooth charting and multi-visit treatment plans. A plan lists the procedures agreed with the patient and tracks which have been done, so a long course of treatment is billed visit by visit.',
    steps: [
      'Open the patient and chart the findings on the tooth chart.',
      'Create a treatment plan with the procedures and their order.',
      'At each visit, mark the procedures done; they are billed as you go.',
    ],
  },
  lab: {
    title: 'From order to released report',
    body: 'Find the patient, search for tests or panels, set the priority and place the order; it lands on the worklist with an invoice already raised. Collection allocates an accession number to each specimen, results are checked against the reference range as you type, and a second person verifies and releases the report.',
    steps: [
      'Order: choose the patient and the tests, tick Bill now for a walk-in, and place the order.',
      'Collect: tick the ordered tests, press Collect and print the tube labels.',
      'Results: open the result sheet and type each value; low and high results are flagged.',
      'Verify and release: results wait in To verify for a second look, then the Released tab lists them with a print button.',
    ],
  },
  'lab.critical': {
    title: 'Critical results nobody has acknowledged',
    body: 'Critical values and findings that nobody has acknowledged yet. A critical result is phoned to the treating doctor and the call is recorded here, with who was told and when.',
    steps: [
      'Open the result and call the doctor named on the order.',
      'Press Acknowledge and record who you spoke to.',
      'Escalate if the doctor cannot be reached; the result stays on the list until it is acknowledged.',
    ],
  },
  'lab.home-collection': {
    title: 'Sample collection at the patient’s address',
    body: 'Visits to collect specimens at home: who asked, where, and which phlebotomist is going. The order is placed as usual; this screen only tracks the visit.',
    steps: [
      'Assign a phlebotomist to each visit and reschedule when the patient asks.',
      'Mark the visit collected once the specimens are back in the lab.',
      'Record a visit that did not happen, with the reason.',
    ],
  },
  'lab.qc': {
    title: 'Quality control',
    body: 'Control lots, Westgard rules and Levey-Jennings charts. Run a control with each batch; a rule violation stops results from that run being released until it is reviewed.',
    steps: [
      'Register a control lot with its target mean and standard deviation.',
      'Enter each control run; the chart and the rule checks update at once.',
      'Review a violation and record the corrective action.',
    ],
  },
  'lab.catalogue': {
    title: 'The test catalogue',
    body: 'Every test has its department, result type, unit, reference range, turnaround time and price. Reference ranges drive the low and high flags when results are typed, so enter them once and every report is checked the same way. Panels bundle tests that are ordered by one name.',
    steps: [
      'Press Add a test: code, name, specimen, result type and unit, turnaround and price.',
      'Set critical low and high limits so a dangerous value is flagged.',
      'Mark a test as sent out when a reference lab runs it, or switch it off without deleting it.',
      'Link each test to a charge item so ordering it raises the right bill.',
    ],
  },
  'lab.reports': {
    title: 'What the laboratory did, for whom and how fast',
    body: 'Volumes, turnaround and revenue for the laboratory over any date range, by test and by doctor.',
    steps: [
      'Choose the report and the date range.',
      'Export the table when you need it outside the system.',
    ],
  },
  radiology: {
    title: 'Modality worklist, reporting and release',
    body: 'Imaging orders arrive from the consultation or are placed here. The technician performs the study, the radiologist writes and verifies the report, and the released report is printed or read from the patient’s record.',
    steps: [
      'Order imaging for a patient, or pick an ordered study from the worklist.',
      'Mark the study performed; a retake sends it back to the technician.',
      'Write the report and release it once verified.',
    ],
  },
  pharmacy: {
    title: 'Sell at the counter',
    body: 'Search a medicine by name or scan its barcode, set the quantity, type the customer’s name for a walk-in and take payment. For cash, the change is worked out from the amount tendered. Prescriptions issued by doctors wait in the prescription queue and are dispensed from the same screen.',
    steps: [
      'Press F3 to jump to the search box and add items to the cart.',
      'Press F2 to take payment; choose the payment mode.',
      'Open the Prescription queue tab to dispense what the doctors issued.',
      'Returns go through the Returns tab and put the batch back on the shelf.',
    ],
  },
  inventory: {
    title: 'Products, stock and purchasing',
    body: 'Drugs and consumables are listed with their brand and generic names, form, strength, pack size, quantity on hand, reorder level and a prescription flag. Receiving a delivery puts stock on the shelf batch by batch; selling always takes the batch that expires first.',
    steps: [
      'Add each distributor once under Suppliers.',
      'Receive goods: store, supplier, their invoice number, then a line per product with batch, expiry, quantity, cost and retail price.',
      'Check Expiry once a week and Reorder before placing a purchase order.',
    ],
  },
  'stock-control': {
    title: 'Adjustments, counts and repricing',
    body: 'Approve adjustments, count the shelves and reprice stock. A stock take is a sheet counted item by item; the differences become adjustments that someone else approves.',
    steps: [
      'Start a stock take for a store and count each item on the sheet.',
      'Post the sheet; the differences wait as adjustments.',
      'Approve or reject each adjustment; approved ones change the quantity on hand.',
    ],
  },
  'online-orders': {
    title: 'Medicine orders from the public directory',
    body: 'Customers who find you in the public directory can order medicines online. Each order waits here until the counter accepts it and prepares it, or declines it with a reason the customer sees.',
    steps: [
      'Open an order to see the items and the customer’s details.',
      'Accept it to reserve the stock and prepare the order for collection or delivery.',
      'Decline with a short reason when an item is not available.',
    ],
  },
  billing: {
    title: 'Invoices and their balances',
    body: 'The tests a doctor ordered and the consultation itself already have their invoice, with its context, payer, status, total and balance; nobody types those. A new invoice is only needed for something nobody else raised.',
    steps: [
      'Filter by status or search by invoice number, patient name or MRN.',
      'Open an invoice to see every charge, then press Collect payment.',
      'Choose cash, card, bank transfer, mobile wallet, online gateway or credit panel; for cash, enter what was tendered and the change is worked out.',
      'The receipt and an A4 bill print from the same screen; partial payments leave the balance on the invoice.',
    ],
  },
  'billing.opd-dues': {
    title: 'Visits with something for the patient to pay',
    body: 'Today’s OPD visits whose consultation fee is not yet paid, so the cashier can settle them before or after the doctor sees the patient. A fee alert on the queue points at the same visit.',
    steps: [
      'Pick the visit from the list.',
      'Press Collect and take the payment as usual.',
      'The fee alert on the queue clears as soon as the payment is recorded.',
    ],
  },
  'billing.shift': {
    title: 'Open a shift before taking payments',
    body: 'A cashier opens a shift with the opening float, the cash already in the drawer. Everything collected from then on is counted against that shift. The shift page updates as payments arrive: what was collected, how many invoices, the cash expected in the drawer and the total by payment mode.',
    steps: [
      'Press Open shift and enter the opening float.',
      'Take payments as usual; the totals update as you go.',
      'At the end of the day, count the drawer note by note and close the shift; the system compares the count with what it expected.',
    ],
  },
  'billing.refunds': {
    title: 'Refunds and credit notes',
    body: 'Money handed back to patients, and reductions of a bill that was issued for too much. A refund is requested by the cashier and approved by someone with refund authority before the money leaves the drawer.',
    steps: [
      'Request a refund from the invoice, with the amount and the reason.',
      'An approver reviews it here and approves or rejects it.',
      'Pay it out; the refund appears on the shift it was paid from.',
      'Raise a credit note when the bill itself was wrong rather than the payment.',
    ],
  },
  'billing.credit-customers': {
    title: 'Customers who took goods on credit',
    body: 'Named customers who took goods or services on credit, what they owe and what they have paid. Use it for regular customers a store sells to on account.',
    steps: [
      'Add a customer with a credit limit.',
      'Sell on credit at the counter by choosing the customer instead of cash.',
      'Record their payments here and watch the balance.',
    ],
  },
  'ipd.dues': {
    title: 'Patients who have left and still owe',
    body: 'Patients who have left the ward and still owe money, with how long ago they left. Discharge is never blocked by an unpaid bill, so this register is how the dues are followed up.',
    steps: [
      'Sort by age or amount to chase the oldest or the largest first.',
      'Open a row to see the bill and record a payment or a write-off.',
    ],
  },
  'billing.charges': {
    title: 'Services, prices and price lists',
    body: 'Every billable service with its list price, the price lists that change that price for a payer or a patient category, packages that bundle services at one price, and the consultation fees charged per doctor.',
    steps: [
      'Add a service with its code, department and list price.',
      'Add a price list for a payer or a category and set the prices that differ from the list.',
      'Build a package when a set of services is sold at one fixed price.',
    ],
  },
  'billing.settings': {
    title: 'Cash, shifts and payment rules',
    body: 'How cashiers work: whether a shift is required before taking money, which payment modes are offered, discount limits and receipt wording. Changing a rule here changes it for every counter.',
    steps: [
      'Review each rule and change only what your facility does differently.',
      'Save; the change applies from the next payment.',
    ],
  },
  insurance: {
    title: 'Claims, policies and what payers owe',
    body: 'The claims list leads because chasing money already earned is the job; the payer master and the aging report are what make chasing it possible. A claim bundles every issued invoice with an unclaimed panel share for one payer.',
    steps: [
      'Assemble a claim for a payer; it pulls every issued invoice with an unclaimed panel share.',
      'Submit the claim and record the remittance when the payer pays.',
      'Watch Aging for claims that are overdue.',
    ],
  },
  accounting: {
    title: 'Ledgers, statements and the entries behind them',
    body: 'The chart of accounts, journals and ledgers. Sales, payments, refunds and supplier payments post themselves; a manual voucher is for everything else, and its debits and credits must balance.',
    steps: [
      'Open a ledger to see the entries behind a balance.',
      'Post a manual voucher for an entry no other screen raises.',
      'Run the trial balance and the statements for the period.',
    ],
  },
  payables: {
    title: 'What is owed to suppliers',
    body: 'What is owed to suppliers, what has been paid, and goods returned to them. Every goods receipt creates a payable; a payment settles chosen receipts, and a return raises a debit note.',
    steps: [
      'Choose a supplier to see their receipts, returns and payments.',
      'Pay a supplier and choose which goods received the payment settles.',
      'Return goods to a supplier; the stock leaves at cost and the supplier is debited.',
    ],
  },
  'accounts.authority': {
    title: 'Who may give a discount or approve a refund',
    body: 'Financial authority says who may give a discount, approve a refund, or write a bill off, and up to what amount. A cashier above their limit is asked for an approver.',
    steps: [
      'Set a limit per role or per person for discounts, refunds and write-offs.',
      'Name who approves anything above a limit.',
    ],
  },
  'accounts.doctor-share': {
    title: 'The doctor’s part of each service',
    body: 'The part of each service’s collected money paid to the doctor who performed it. Set a default for a whole category and override it per service or per doctor; only collected money is shared, so the payout follows the cash.',
    steps: [
      'Set the default share for each category.',
      'Override a service or a doctor where the agreement differs.',
      'Payouts are computed from these rates under Doctor payouts.',
    ],
  },
  'accounting.payouts': {
    title: 'Doctor payouts',
    body: 'Shares computed from money the hospital has actually collected. A payout run gathers every collected line with a share for the period, adds any adjustments, and produces the amount to pay each doctor.',
    steps: [
      'Compute a payout for the period and the doctors you want.',
      'Add an adjustment for money owed that no bill line shows.',
      'Approve and mark the payout paid.',
    ],
  },
  ambulance: {
    title: 'Fleet, dispatch and trip log',
    body: 'Vehicles and drivers, dispatch of a trip, and the log of every trip with its charge.',
    steps: [
      'Press Dispatch, choose the vehicle and driver, and record where to.',
      'Close the trip when the vehicle is back; the trip charge is billed to the patient.',
    ],
  },
  dietary: {
    title: 'The kitchen sheet',
    body: 'Every in-patient, their diet and their allergies, ward by ward, so the kitchen cooks the right meal for the right bed.',
    steps: [
      'Choose the ward at the top.',
      'Print the sheet for the kitchen or tick each meal as delivered.',
    ],
  },
  housekeeping: {
    title: 'Bed cleaning and the ward round',
    body: 'Beds released by a discharge wait as awaiting cleaning. Housekeeping marks them cleaned and they return to the bed board as vacant, so the next admission can use them.',
    steps: [
      'Filter to awaiting cleaning to see what needs doing.',
      'Mark a bed cleaned when it is ready; it becomes vacant on the bed board.',
    ],
  },
  tasks: {
    title: 'Who is doing what, and by when',
    body: 'Tasks assigned to support staff, with a due time and a patient where one applies. A task stays open until the person it was given to marks it done.',
    steps: [
      'Assign a task with a title, the person responsible and a due time.',
      'Mark your own tasks done as you finish them.',
    ],
  },
  notifications: {
    title: 'Messages to patients and staff',
    body: 'What was sent to patients and staff, and the wording it was sent with. Templates decide the wording; anything in double braces is filled in when the message goes out.',
    steps: [
      'Edit a template to change what an appointment reminder or a result-ready message says.',
      'Send a one-off message to a single number or address.',
      'Check the log when a patient says they never received a message.',
    ],
  },
  feedback: {
    title: 'Feedback and complaints',
    body: 'What patients said, and what was done about it. A complaint is a ticket: logged, assigned, resolved, with the outcome recorded.',
    steps: [
      'Log a complaint with who raised it and what happened.',
      'Assign it to the person who will handle it.',
      'Record the resolution and close it.',
    ],
  },
  assets: {
    title: 'Equipment, maintenance and breakdowns',
    body: 'The equipment register, preventive schedules and breakdowns. Warranty and AMC dates decide what a breakdown costs; the nightly job turns a due schedule into a ticket.',
    steps: [
      'Add an asset with its purchase, warranty and AMC dates.',
      'Set a preventive maintenance schedule for it.',
      'Report a breakdown; it takes the machine out of service until the ticket is resolved.',
    ],
  },
  people: {
    title: 'Who works here and what they may do',
    body: 'Everyone who works here is on one list, whether they sign in, are on the payroll, or both. A login decides what a person may open; an HR record carries their contract, pay, rosters and leave. The two stay separate underneath, so a locum can be paid without ever signing in, and a shared account never hides who did what.',
    steps: [
      'Press Add person and fill in the name and contact details.',
      'Switch on “This person signs in” to give them a username, a temporary password and one or more roles.',
      'Mark doctors as doctors, give them a licence number so prescriptions print correctly, and switch on “Can be booked for appointments” for anyone the desk may book.',
      'Switch on “Has an HR record” for contract, pay and registration expiry, or link a record that already exists.',
      'Use the Roles tab to see what each role may do, and clone one to make a variant.',
    ],
  },
  'admin.branches': {
    title: 'Sites, departments and units',
    body: 'Branches are the sites you operate; departments and wards sit inside them. Add the standard departments for the modules you hold rather than typing them one by one.',
    steps: [
      'Add a branch with its address, phone and timezone.',
      'Press Add standard departments to create the usual ones for your modules.',
      'Add the specialties your doctors practise so they print under the right heading.',
    ],
  },
  'admin.settings': {
    title: 'Make it yours',
    body: 'Set the display name and colour, the currency, the follow-up window, the session timeout and the ward stock model. Numbering, laboratory, integrations and update information have their own tabs. Licensed modules are listed at the bottom; your provider switches them on, and the menu follows.',
    steps: [
      'Change a setting and press Save on that card.',
      'Require two-factor authentication for administrative and financial roles under Security.',
      'Check Numbering before the first invoice so the series match your stationery.',
    ],
  },
  'admin.listing': {
    title: 'What patients see when they search for you',
    body: 'The public listing is what patients find when they search the directory by city: your name, branches, hours and what can be booked online. Only the details on this screen are ever shown publicly.',
    steps: [
      'Fill in the description, hours and services for each branch.',
      'Switch on online appointment requests and medicine orders if you want them.',
      'Publish; the listing appears in the directory.',
    ],
  },
  'admin.audit': {
    title: 'Every change is on record',
    body: 'Every create, update and delete on a clinical or financial record is listed with the time, the person, the table and the address they were signed in from. The Hash chain intact badge means no entry has been altered or removed since it was written.',
    steps: [
      'Filter by table, by action or by person.',
      'Open an entry to see the values before and after the change.',
    ],
  },
  support: {
    title: 'Raise an issue with the people who look after this system',
    body: 'A ticket goes to the provider that runs this system for you. Describe what happened, when, and on which screen; they reply here and you are notified.',
    steps: [
      'Press New ticket and describe the problem, with the screen and the time.',
      'Follow the replies on the ticket and close it when it is resolved.',
    ],
  },
  'account.security': {
    title: 'Your password and two-factor sign-in',
    body: 'Security holds the two things that protect your own account: your password and an authenticator app for two-factor sign-in. Turning two-factor on asks for a six-digit code at every sign-in; roles that handle money or administration cannot turn it off.',
    steps: [
      'To change your password, enter the current one, then the new one twice, and press Change password. Every device is signed out, this one included.',
      'Press Turn on under two-factor sign-in, add the secret key to Google Authenticator, Authy or 1Password, and confirm with the code it shows.',
      'Changed phone? Press Re-enrol with a code from the old authenticator, then confirm with the new one. Lost it? An administrator resets it from People.',
    ],
  },
  'people.tab.people': {
    title: 'The people directory',
    body: 'Every person with a login, an HR record or both. The Login and HR record badges show which halves exist; open a person to add the missing half or link an existing record.',
    steps: [
      'Press Add person and choose whether they sign in, have an HR record, or both.',
      'Open a person to change roles, unlock the account, upload a signature or edit their contract.',
      'Use Permissions on a row for a one-off grant or deny beyond what their roles give.',
    ],
  },
  'people.tab.roles': {
    title: 'Roles and permissions',
    body: 'A role is a named set of permissions. Ready-made roles cover doctors, nurses, cashiers, pharmacists and more; clone one to make a variant rather than starting from nothing.',
    steps: [
      'Open a role to see what it may do and how far its reach goes.',
      'Duplicate a role and adjust the copy when a job needs a little more or less.',
    ],
  },
  'people.tab.roster': {
    title: 'Rosters and shifts',
    body: 'A roster covers a period for a ward or department. Assign morning, evening and night shifts to people with an HR record; overlapping shifts and approved leave are refused.',
    steps: [
      'Press New roster and give it a name and a period.',
      'Assign shifts person by person, then Publish when the roster is final.',
    ],
  },
  'people.tab.attendance': {
    title: 'Attendance',
    body: 'Lateness and overtime are measured against the rostered shift, so a roster is the first thing to have in place. The tiles summarise the day you are looking at.',
    steps: [
      'Pick the day.',
      'Press Record a day to mark someone present, absent, on leave or on a half day.',
    ],
  },
  'people.tab.leave': {
    title: 'Leave',
    body: 'Leave requests wait here for approval. Approved leave is paid and blocks roster shifts on those days.',
    steps: [
      'Filter by status to see what is pending.',
      'Approve or reject a request from its row; request leave on someone’s behalf with Request leave.',
    ],
  },
  'people.tab.payroll': {
    title: 'Payroll',
    body: 'A payroll run builds draft payslips for a period from basic pay, allowances, deductions, overtime and tax. Absent days are deducted pro-rata and approved leave is paid. Processing a run posts the liability to the ledger and freezes the payslips.',
    steps: [
      'Press Run payroll and choose the period, the overtime rate and the tax percentage.',
      'Check the draft payslips, then Process; download the bank transfer file afterwards.',
    ],
  },
  'billing.charges.tab.charges': {
    title: 'Services',
    body: 'Every billable service with its code, department and list price. Lab tests and imaging studies link to a service here so ordering them raises the right bill.',
    steps: [
      'Add a service with its code, department and list price.',
      'Switch a service off rather than deleting it when it is no longer offered.',
    ],
  },
  'billing.charges.tab.tariffs': {
    title: 'Price lists',
    body: 'A price list prices services for a payer or a patient category. When a bill is raised, the most specific list that applies wins; anything it does not mention falls back to the list price.',
    steps: [
      'Add a price list and choose the payer or the category it applies to.',
      'Set a price only for the services that differ from the list price.',
    ],
  },
  'billing.charges.tab.packages': {
    title: 'Packages',
    body: 'One fixed price covering a set of services, such as a delivery package or a health check.',
    steps: [
      'Create a package and add the services it includes.',
      'Set the package price; it replaces the sum of the component prices.',
    ],
  },
  'billing.charges.tab.consultation': {
    title: 'Consultation fees',
    body: 'When the consultation fee is charged and whether the consultation waits for it to be paid. The fee itself is the price of a consultation service that lists the doctor.',
    steps: [
      'Add each doctor to a consultation service under Services & prices; the Doctors section of the service can give that doctor their own price.',
      'Choose when the fee is charged; the queue shows a fee alert until it is paid.',
      'The doctor decides a follow-up when closing the consultation: free, paid or discounted.',
    ],
  },
  'insurance.tab.claims': {
    title: 'Claims',
    body: 'Every claim with its payer, amount and status. Assemble a claim to pull every issued invoice with an unclaimed panel share for a payer.',
    steps: [
      'Press Assemble a claim and choose the payer and the period.',
      'Submit it and track it until the remittance arrives.',
    ],
  },
  'insurance.tab.aging': {
    title: 'Aging',
    body: 'How long each payer has owed each claim, in buckets. The oldest buckets are the ones to chase.',
    steps: [
      'Sort by the oldest bucket.',
      'Open a payer to see the claims behind the total.',
    ],
  },
  'insurance.tab.reports': {
    title: 'Revenue cycle',
    body: 'How much was billed to payers, how much was claimed, paid and rejected, for any date range.',
    steps: ['Choose the period and the payer.', 'Export the table for the finance team.'],
  },
  'insurance.tab.preauth': {
    title: 'Pre-authorisations',
    body: 'Services that need the payer’s approval before they are done. A request lists the services and waits for an answer; an approved line can be billed to the payer.',
    steps: [
      'Open a pending request to see which services it covers.',
      'Record the payer’s answer line by line, or send it electronically where the payer supports it.',
    ],
  },
  'insurance.tab.payments': {
    title: 'Payer payments',
    body: 'Remittances received from payers, matched against claims. Each remittance settles the claim lines the payer paid and records what they rejected.',
    steps: [
      'Record a remittance with the payer, the amount and the claims it settles.',
      'Mark rejected lines so they can be resubmitted or written off.',
    ],
  },
  'insurance.tab.requests': {
    title: 'Insurer requests',
    body: 'Questions and requests for information the insurer sent back on a claim or a pre-authorisation. Each stays open until it is answered.',
    steps: ['Open a request to read what the insurer asked.', 'Reply with the information or the document requested.'],
  },
  'insurance.tab.payers': {
    title: 'Payers',
    body: 'Insurance companies, panels and TPAs you bill. Co-pay and pre-authorisation rules set here become the defaults at the desk when a patient uses that payer.',
    steps: [
      'Add a payer with its contact details and payment terms.',
      'Set the co-pay and which services need pre-authorisation.',
      'Link a price list under Services & prices if the payer has agreed prices.',
    ],
  },
  'inventory.tab.products': {
    title: 'Products',
    body: 'Drugs and consumables with their brand and generic names, form, strength, pack size, quantity on hand, reorder level and a prescription flag.',
    steps: ['Search by name, brand or code.', 'Add a product once; stock arrives through Receive goods.'],
  },
  'inventory.tab.stock': {
    title: 'Stock on hand',
    body: 'Quantity on hand per product and per batch, store by store. The quantity drops the moment a sale is completed, batch by batch.',
    steps: ['Choose the store at the top.', 'Open a product to see its batches and expiry dates.'],
  },
  'inventory.tab.expiry': {
    title: 'Expiry',
    body: 'What expires in 30, 60 and 90 days, and what has already expired. Returning near-dated stock to the distributor is far cheaper than writing it off.',
    steps: ['Check this tab once a week.', 'Return near-dated stock through Supplier payables, or write off expired stock through Stock control.'],
  },
  'inventory.tab.reorder': {
    title: 'Reorder',
    body: 'Products that have fallen below their reorder level, with the suggested quantity.',
    steps: ['Review the list before placing a purchase order.', 'Raise a purchase order for the supplier from the selected lines.'],
  },
  'inventory.tab.valuation': {
    title: 'Valuation',
    body: 'The stock at cost and at retail, with your margin, store by store.',
    steps: ['Choose the store and the date.', 'Export the figures for the accountant at month end.'],
  },
  'inventory.tab.suppliers': {
    title: 'Suppliers',
    body: 'Every goods receipt and purchase order is raised against a supplier. Add each distributor once, with a code, a contact person, a phone number and payment terms.',
    steps: ['Add a supplier before the first receipt from them.', 'Untick Still buying from them to retire a supplier without losing their history.'],
  },
  'inventory.tab.purchase-orders': {
    title: 'Purchase orders',
    body: 'What has been ordered from each supplier and what has arrived against it. Receiving against an order fills in the lines for you.',
    steps: ['Raise an order with the supplier and the lines.', 'Receive goods against it when the delivery arrives.'],
  },
  'inventory.tab.goods-received': {
    title: 'Goods received',
    body: 'Every delivery received, with the supplier invoice number and the batches it put on the shelf. Batch and expiry are required, so selling always takes the batch that expires first.',
    steps: [
      'Press Receive goods: store, supplier and their invoice number.',
      'Add a line per product with batch, expiry, quantity, cost and retail price.',
      'Press Receive; the stock is on the shelf and a payable is raised for the supplier.',
    ],
  },
  'inventory.tab.indents': {
    title: 'Ward indents',
    body: 'A ward asking the pharmacy for stock. The ward raises the indent; the pharmacy issues against it and the stock moves to the ward store.',
    steps: ['Raise an indent from the ward with the items and quantities.', 'Issue it from the pharmacy; the quantities move between stores.'],
  },
};

export const HELP_MESSAGES: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(COPY).flatMap(([key, copy]) => [
    [`help.${key}.title`, copy.title],
    [`help.${key}.body`, copy.body],
    [`help.${key}.steps`, copy.steps.join('\n')],
  ]),
);

export function helpMessage(key: string): string | undefined {
  return HELP_MESSAGES[key];
}
